import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  config,
  DATE_OPTIONS,
  DateOptions,
  MarketplaceItemAttribute,
  MarketplaceItemAttributeDisplayType,
  MarketplaceItemAttributeValue,
  MarketplaceItemMappingAttribute,
  toNumber,
} from '@fgb/core';
import { DirectivesModule, ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import * as moment from 'moment';
@Component({
  selector: 'fgbcl-attribute-field',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule, FGBSharedModule, DirectivesModule],
  templateUrl: './attribute-field.component.html',
  styleUrl: './attribute-field.component.scss',
})
export class AttributeFieldComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() attributes: MarketplaceItemAttribute[] = [];
  @Input() attributeMappings: MarketplaceItemMappingAttribute[] = [];
  @Output() attributeChange = new EventEmitter<MarketplaceItemAttributeValue>();
  selectedAttribute: number;
  selectedAttributeName: string;
  control: AbstractControl | any;
  innerValue: MarketplaceItemAttribute[] = [];
  ScreenType = ScreenType;

  private _onChangeCallback = (_: number) => {};
  private _onTouchedCallback = () => {};
  private readonly renderer = inject(Renderer2);

  constructor(
    @Optional() @Self() private ngControl: NgControl,
    @Inject(DATE_OPTIONS) private dateOptions: DateOptions,
    private elementRef: ElementRef
  ) {
    if (this.ngControl) this.ngControl.valueAccessor = this;
  }

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit(): void {
    if (this.ngControl) {
      this.control = this.ngControl.control;
      this.control.updateValueAndValidity();
    }
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.attributeMappings && !changes.attributeMappings.firstChange) {
      this.init();
    }
  }

  /* ================================
   * Public API
   * ================================ */

  writeValue(value: number): void {
    this.selectedAttribute = value;
    const attribute = this.innerValue.find((attr) =>
      attr.Values.find((val) => val.AttributeMappingId === toNumber(this.selectedAttribute))
    );
    const attributeMapping = attribute?.Values.find(
      (val: MarketplaceItemAttributeValue) => val.AttributeMappingId === toNumber(this.selectedAttribute)
    );
    this.selectedAttributeName = attributeMapping?.DisplayName || '';
  }

  registerOnChange(fn: any): void {
    this._onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn;
  }

  onAttributeChange(): void {
    const attribute = this.innerValue.find((attr) =>
      attr.Values.find((val: MarketplaceItemAttributeValue) => val.AttributeMappingId === toNumber(this.selectedAttribute))
    );
    const attributeMapping = attribute?.Values.find(
      (val: MarketplaceItemAttributeValue) => val.AttributeMappingId === toNumber(this.selectedAttribute)
    );
    this.selectedAttributeName = attributeMapping?.DisplayName || '';
    this._onChangeCallback(toNumber(this.selectedAttribute));
    this._onTouchedCallback();
    this.attributeChange.emit(attributeMapping);
  }

  onCanvasAttributeSelect(attributeMappingId: number): void {
    this.selectedAttribute = attributeMappingId;
    this.onAttributeChange();
  }

  addStyleBackdrop() {
    const backdrop = this.elementRef.nativeElement.querySelector('.modal-backdrop');
    if (!backdrop) return;
    this.renderer.setStyle(backdrop, 'z-index', '999');
    this.renderer.setStyle(backdrop, 'opacity', '0.15');
  }

  /* ================================
   * Private Helpers
   * ================================ */

  private init(): void {
    this.innerValue = this.attributes.map((attr: MarketplaceItemAttribute) => ({
      ...attr,
      DisplayName: this.getLocalisedName(attr),
      Values: attr.Values.map((val: any) => {
        const attrMapping = this.getAttributeMapping(val.RewardValueId);
        const { StockCount, AttributeMappingId, AvailableStockCount, UnlimitedStock } = attrMapping || {};
        return {
          ...val,
          DisplayName:
            attr.DisplayType === MarketplaceItemAttributeDisplayType.Date && !val.DisplayName.trim() // If the attribute is of type Date and the DisplayName is empty, treat the DisplayName as a date value and convert it to local date, otherwise get the localised name as usual
              ? this.getLocalDate(val.AttributeValue)
              : this.getLocalisedName(val),
          StockCount,
          Label: this.getLocalisedName(attr),
          AttributeMappingId,
          AvailableStockCount,
          UnlimitedStock,
        };
      }),
    }));
  }

  /** Get Localised Name by LCID */
  private getLocalisedName(_: any): string {
    const lcid = toNumber(localStorage.getItem('lcid') || config.defaultLcid);
    return _.Locales.find((x: any) => x.LCID === lcid)?.DisplayName || _.Locales[0].DisplayName;
  }

  /** Get Local Date from UTC */
  private getLocalDate(value: string): string {
    return moment.utc(value).local().format(this.dateOptions.LongDateFormat);
  }

  /** Get Attribute Mapping by Reward Value ID */
  private getAttributeMapping(rewardValueId: number): MarketplaceItemMappingAttribute | undefined {
    return this.attributeMappings.find((mapping: MarketplaceItemMappingAttribute) =>
      mapping.AttributeValues.includes(rewardValueId)
    );
  }
}
